import type {
  TokenPair,
  IdTokenPayload,
  TokenStorageStrategy,
} from "../../types";
import { decodeJwtExpiry } from "../jwt-decode";
import { getEnvIndexBaseUrl } from "../../../api/osp/constants";

/**
 * localStorage 存储策略 — 兼容现有 key 格式
 *
 * 读取支持两种格式:
 *   - {data: {accessToken, refreshToken}, metadata: {updatedAt}} (SDK 格式)
 *   - {accessToken, refreshToken} (简化格式)
 *
 * 写入统一使用 SDK 兼容格式（确保发版前后数据可互读）
 */
export class LocalStorageStrategy implements TokenStorageStrategy {
  private readonly credentialsKey: string;
  private readonly idTokenKey: string;

  constructor() {
    const indexBaseUrl = getEnvIndexBaseUrl(
      process.env.NEXT_PUBLIC_ENV ?? "dev",
    );
    this.credentialsKey = `osp.${indexBaseUrl}.credentials`;
    this.idTokenKey = `osp.${indexBaseUrl}.idToken`;
  }

  getTokens(): TokenPair | null {
    try {
      const raw = localStorage.getItem(this.credentialsKey);
      if (!raw) {
        return null;
      }
      const parsed = JSON.parse(raw);
      const data = parsed?.data ?? parsed;
      if (!data?.accessToken) {
        return null;
      }
      return {
        accessToken: data.accessToken,
        refreshToken: data.refreshToken ?? "",
        expiresAt: decodeJwtExpiry(data.accessToken),
      };
    } catch {
      return null;
    }
  }

  setTokens(tokens: TokenPair): void {
    try {
      const payload = {
        data: {
          accessToken: tokens.accessToken,
          refreshToken: tokens.refreshToken,
        },
        metadata: { updatedAt: Date.now() },
      };
      localStorage.setItem(this.credentialsKey, JSON.stringify(payload));
      this.syncLegacyCache(tokens);
    } catch {
      /* storage full or unavailable */
    }
  }

  /**
   * 同步写入 OSP_LOGIN_CACHE_V1，防止 login.ts initFromCache() 将新 token 误判为
   * "orphan SDK credentials" 而删除。该 IIFE 在模块加载时同步运行，先于 AuthOrchestrator。
   */
  private syncLegacyCache(tokens: TokenPair): void {
    try {
      const legacyCache = {
        accessToken: tokens.accessToken,
        refreshToken: tokens.refreshToken,
        expiresAt: tokens.expiresAt,
      };
      localStorage.setItem("OSP_LOGIN_CACHE_V1", JSON.stringify(legacyCache));
    } catch {
      /* ignore */
    }
  }

  getIdToken(): IdTokenPayload | null {
    try {
      const raw = localStorage.getItem(this.idTokenKey);
      if (!raw) {
        return null;
      }
      const parsed = JSON.parse(raw);
      const idToken = parsed?.data ?? parsed;
      if (typeof idToken !== "string") {
        return null;
      }
      return { idToken, expiresAt: decodeJwtExpiry(idToken) };
    } catch {
      return null;
    }
  }

  setIdToken(payload: IdTokenPayload): void {
    try {
      localStorage.setItem(
        this.idTokenKey,
        JSON.stringify({ data: payload.idToken }),
      );
    } catch {
      /* ignore */
    }
  }

  clear(): void {
    localStorage.removeItem(this.credentialsKey);
    localStorage.removeItem(this.idTokenKey);
    localStorage.removeItem("OSP_LOGIN_CACHE_V1");
    localStorage.removeItem("OSP_AUTH_CACHE_V1");
    localStorage.removeItem("OWNER_INFO_CACHE");
  }
}
